import { TemplateElement, html, defineElement } from '@webtides/element-js';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock.es6';
import { svgUse } from '../../webresources-sources/js/util/icon.js';

/**
 * @property {boolean} open
 * @property {'center' | 'bottom' | 'fullscreen'} placement
 */
export default class ModalDialog extends TemplateElement {
    constructor() {
        super({ shadowRender: true });
    }

    properties() {
        return {
            open: false,
            placement: 'center',
        };
    }

    get fullscreenMode() {
        return this.placement === 'fullscreen';
    }

    watch() {
        return {
            open: (open) => {
                if (open) {
                    this.$refs.dialog.showModal();
                    if (this.fullscreenMode) this.$refs.form.requestFullscreen();
                    this.dispatch('modal-open', true, true, true);
                    disableBodyScroll(this.$refs.dialog, {
                        reserveScrollBarGap: true,
                    });
                } else {
                    this.$refs.dialog.close();
                    if (this.fullscreenMode && globalThis.document?.fullscreenElement) {
                        globalThis.document?.exitFullscreen();
                    }
                    this.dispatch('modal-close', true, true, true);
                    enableBodyScroll(this.$refs.dialog);
                }
            },
        };
    }

    events() {
        return {
            this: {
                click: (event) => {
                    if (event.target.hasAttribute('data-close-modal')) {
                        this.open = false;
                    }
                },
            },
            dialog: {
                close: () => {
                    this.open = false;
                },
                cancel: () => {
                    this.open = false;
                },
                click: (event) => {
                    if (event.target.nodeName === 'DIALOG') {
                        this.$refs.dialog.close('dismiss');
                    }
                },
            },
            'slot[name="close"]': {
                click: () => {
                    this.open = false;
                },
            },
            form: {
                fullscreenchange: (event) => {
                    if (this.fullscreenMode && !globalThis.document?.fullscreenElement) {
                        this.open = false;
                    }
                },
            },
        };
    }

    template() {
        return html`
            <dialog ref="dialog" placement="${this.placement}">
                <form ref="form" method="dialog" class="content">
                    <div class="close">
                        <slot name="close">
                            <button class="close-button" value="cancel">
                                ${svgUse({
                                    name: 'icon-close',
                                    cssClasses: 'icon-close',
                                })}
                            </button>
                        </slot>
                    </div>
                    <main>
                        <slot></slot>
                    </main>
                </form>
            </dialog>
        `;
    }

    styles() {
        return [
            `
            :host {
                display: block;
                width: 100%;
                height: 100%;
                position: fixed;
            }
            
            dialog {
                position: fixed;
                background: transparent;
                border: 0;
                margin: 0;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                transition: transform 500ms ease-out;
                transform: translateY(24px);
            }
            
            dialog:not([open]) {
                pointer-events: none;
                opacity: 0;
            }
            
            dialog[open] {
                transform: translateY(0);
            }
            
            dialog .content {
                background: #001F47;
                color: white;
                position: relative;
            }
            
            dialog .close {
                position: absolute;
                z-index: 10;
                top: 16px;
                right: 16px;
            }
            
            dialog .close button {
                background: none;
                border: 0;
                color: var(--accent-hover-color);
            }
            
            dialog .close .icon-close {
                width: 24px;
                height: 24px;
            }
            
            dialog[placement="center"] {
                align-items: center;
            }
            
            dialog[placement="bottom"] {
                align-items: flex-end;
            }
            
            dialog[placement="fullscreen"] {
                max-width: 100vw;
                max-height: 100vh;
                border: 0;
                margin: 0;
                padding: 0;
                width: 100vw;
                height: 100vh;
            }
            
            dialog[placement="fullscreen"] .content {
                width: 100%;
                height: 100%;
            }
            
            dialog[placement="fullscreen"] .content main {
                height: 100%;
                overflow: auto;
            }
        `,
        ];
    }
}

defineElement('modal-dialog', ModalDialog);
